import Button from "atoms/buttons/button";
import CheckboxInput from "atoms/forms/checkbox-input";
import EmailMultiInput from "molecules/form-fields/email-multi-input";
import Heading from "atoms/typography/heading";
import Icon from "atoms/icons/icon";
import Loader from "atoms/loaders/loader";
import Modal from "molecules/modals/modal";
import Paragraph from "atoms/typography/paragraph";
import React, { useEffect, useState } from "react";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { CollectionUtils } from "@rsm-hcd/javascript-core";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import { ModalTypes } from "molecules/constants/modal-types";
import { PageAccessRecipientsTable } from "./page-access-recipients-table";
import { RadioButtonProps } from "atoms/forms/radio-button-input";
import { RadioListInputFormField } from "molecules/form-fields/radio-input-form-field";
import { ToastManager } from "utilities/toast/toast-manager";
import { useLocalization } from "utilities/hooks/use-localization";
import { useManagePageAccess } from "organisms/modals/page-access-modal/hooks/use-manage-page-access";
import { ConfirmationModal } from "molecules/modals/confirmation-modal";
import useModalActions from "utilities/hooks/use-modal-actions";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import useBreakpoint, {
    BreakpointComparer,
} from "utilities/hooks/use-breakpoint";
import { ModalCloseButtonStyle } from "molecules/constants/modal-close-button-style";

enum PageAccessTarget {
    Individuals,
    Public,
}

interface PageAccessModalProps {
    isOpen: boolean;
    onClose: () => void;
    pageId: number;
    onSaveSuccess: (isNewlyPublic: boolean) => void;
}

const CSS_CLASS_NAME: string = "c-page-access-modal";
const GET_RECIPIENTS_FORBIDDEN =
    "Web.PageAccessRecipientsController.GetRecipients.Forbidden";
const PAGE_SHARE_EMAIL_ERRORS = "PAGE_SHARE_EMAIL_ERRORS";

export const PageAccessModal: React.FC<PageAccessModalProps> = ({
    isOpen,
    onClose,
    pageId,
    onSaveSuccess,
}: PageAccessModalProps): JSX.Element => {
    const [emailsToAdd, setEmailsToAdd] = useState<string[]>([]);
    const [target, setTarget] = useState<PageAccessTarget>(
        PageAccessTarget.Individuals
    );
    const [isCloningPermitted, setIsCloningPermitted] =
        useState<boolean>(false);

    const {
        isOpen: isConfirmationModalOpen,
        handleClose: handleConfirmationModalClose,
        handleOpen: handleConfirmationModalOpen,
    } = useModalActions();
    const { t } = useLocalization();
    const {
        accessRecipients,
        clearErrors,
        errors,
        isPubliclyAccessible: persistedIsPubliclyAccessible,
        isCloningPermitted: persistedCloningPermitted,
        publicPageLink,
        loading,
        saveChanges,
        stageNewRecipients,
        removeRecipient,
        refreshPageAccess,
        clearUnpersistedChanges,
    } = useManagePageAccess(pageId);

    const isPubliclyAccessible = target === PageAccessTarget.Public;
    const pageAccessTargetOptions: Array<RadioButtonProps<PageAccessTarget>> = [
        {
            checked: !isPubliclyAccessible,
            label: t("individualsWithAccess"),
            name: "individual-page-access-target",
            value: PageAccessTarget.Individuals,
        },
        {
            checked: isPubliclyAccessible,
            label: t("everyoneHasAccess"),
            name: "public-page-access-target",
            value: PageAccessTarget.Public,
        },
    ];

    const resetFields = () => {
        setTarget(
            persistedIsPubliclyAccessible
                ? PageAccessTarget.Public
                : PageAccessTarget.Individuals
        );
        setIsCloningPermitted(persistedCloningPermitted);
    };

    const updatePageAccessDetails = async () => {
        const updateSuccessful = await saveChanges(
            isPubliclyAccessible,
            isPubliclyAccessible ? false : isCloningPermitted
        );

        if (updateSuccessful) {
            ToastManager.success(
                t("successActionResourcePossessive", {
                    action: t("updated"),
                    resource: t("pageAccess"),
                })
            );
        }

        const newlyPublic =
            isPubliclyAccessible && !persistedIsPubliclyAccessible;

        onSaveSuccess(newlyPublic);

        onClose();
    };

    const handleAccessTargetChange = (newValue) => {
        if (newValue == null) {
            return;
        }

        setTarget(newValue);
    };

    const handleCloningPermittedChange = () => {
        setIsCloningPermitted(!isCloningPermitted);
    };

    const handleAddClick = () => {
        stageNewRecipients(emailsToAdd);

        setEmailsToAdd([]);
    };

    const handleCancelClick = () => {
        handleConfirmationModalClose();
        onClose();
    };

    const handleEmailsToAddChange = (newValues: string[]) => {
        setEmailsToAdd(newValues);
    };

    const handleSaveClick = () => {
        updatePageAccessDetails();
    };

    useEffect(() => {
        setTarget(
            persistedIsPubliclyAccessible
                ? PageAccessTarget.Public
                : PageAccessTarget.Individuals
        );
    }, [persistedIsPubliclyAccessible]);

    useEffect(() => {
        setIsCloningPermitted(persistedCloningPermitted);
    }, [persistedCloningPermitted]);

    useEffect(() => {
        if (CollectionUtils.isEmpty(errors)) {
            return;
        }

        if (errors.some((e) => e.includes(GET_RECIPIENTS_FORBIDDEN))) {
            ToastManager.error("TODO: This action is not permitted.");
            clearErrors();
            onClose();
            return;
        }

        const emailError = errors.find((e) =>
            e.includes(PAGE_SHARE_EMAIL_ERRORS)
        );
        if (emailError) {
            const emails = emailError.replace(PAGE_SHARE_EMAIL_ERRORS, "");

            ToastManager.error(
                t("page-share-error", {
                    emails,
                })
            );
            clearErrors();
            return;
        }

        ToastManager.error(
            t("errors-actionResource", {
                action: t("updating"),
                resource: t("pageAccess"),
            })
        );
        clearErrors();
    }, [clearErrors, errors, onClose, t]);

    const handleCopyPageLinkClick = () => {
        navigator.clipboard.writeText(publicPageLink);
        ToastManager.success(t("textCopied"));
    };

    const noRecipientsToAdd = emailsToAdd.length === 0;

    const isMobile = useBreakpoint(
        Breakpoints.SmallTablet,
        BreakpointComparer.MaxWidth
    );
    const modalType = isMobile ? ModalTypes.Bottom : ModalTypes.Base;
    const showShareLink =
        persistedIsPubliclyAccessible && target === PageAccessTarget.Public;

    // Refreshes access details on modal open to ensure it reflects latest changes
    useEffect(() => {
        if (isOpen) refreshPageAccess();
        if (!isOpen) clearUnpersistedChanges();
    }, [isOpen, refreshPageAccess, clearUnpersistedChanges]);

    return (
        <Modal
            closeButtonStyle={ModalCloseButtonStyle.Hidden}
            closeDialog={handleConfirmationModalOpen}
            cssClassName={CSS_CLASS_NAME}
            isVisible={isOpen}
            onCloseComplete={resetFields}
            label={t("managePageAccess")}
            type={modalType}>
            <Heading cssClassName={`${CSS_CLASS_NAME}__heading`}>
                {t("managePageAccess")}
                <Button
                    accessibleText={t("close")}
                    cssClassName={"-modal-close -icon"}
                    onClick={handleConfirmationModalOpen}
                    size={ButtonSizes.Medium}
                    style={ButtonStyles.None}>
                    <Icon type={Icons.Close} size={IconSizes.Large} />
                </Button>
            </Heading>
            <div className={`${CSS_CLASS_NAME}__content`}>
                {loading ? (
                    <Loader />
                ) : (
                    <>
                        <div
                            className={`${CSS_CLASS_NAME}__individual-access-fields ${isPubliclyAccessible ? "--disabled" : ""}`}>
                            <EmailMultiInput
                                cssClassName={`${CSS_CLASS_NAME}__share-recipient-input`}
                                label={t("addPeople")}
                                disabled={isPubliclyAccessible}
                                emails={emailsToAdd}
                                onEmailsChanged={handleEmailsToAddChange}
                                isValid={true}
                                suggestTeamMembers={true}
                                placeholder={
                                    isPubliclyAccessible
                                        ? " "
                                        : t("email-multi-input-placeholder")
                                }
                            />
                            <Button
                                cssClassName={`${CSS_CLASS_NAME}__addButton`}
                                accessibleText={t("add")}
                                disabled={
                                    isPubliclyAccessible || noRecipientsToAdd
                                }
                                onClick={handleAddClick}
                                style={ButtonStyles.Tertiary}>
                                {t("add")}
                            </Button>
                        </div>
                        <div
                            className={`${CSS_CLASS_NAME}__accessRadioSelection`}>
                            <RadioListInputFormField
                                label={t("whoHasAccess")}
                                onValueChange={handleAccessTargetChange}
                                options={pageAccessTargetOptions}
                                selectedValue={target}
                                alignHorizontally={true}
                            />
                        </div>
                        {showShareLink && (
                            <div
                                className={`${CSS_CLASS_NAME}__public-access-fields`}>
                                <div
                                    className={`${CSS_CLASS_NAME}__public-access-link`}>
                                    <div
                                        className={`${CSS_CLASS_NAME}__public-access-url`}>
                                        {publicPageLink}
                                    </div>
                                    <Button
                                        cssClassName={`${CSS_CLASS_NAME}__copyButton`}
                                        onClick={handleCopyPageLinkClick}
                                        style={ButtonStyles.Tertiary}>
                                        {t("copyLink")}
                                    </Button>
                                </div>
                                <Paragraph
                                    cssClassName={`${CSS_CLASS_NAME}__public-access-note`}>
                                    {t("managePageAccess-public-access-info")}
                                </Paragraph>
                            </div>
                        )}
                        {!isPubliclyAccessible && (
                            <>
                                <PageAccessRecipientsTable
                                    loading={loading}
                                    recipients={accessRecipients}
                                    removeRecipient={removeRecipient}
                                />
                            </>
                        )}
                    </>
                )}
            </div>
            <div className={`${CSS_CLASS_NAME}__actions`}>
                <div className={`${CSS_CLASS_NAME}__actions-left`}>
                    {!loading && !isPubliclyAccessible && (
                        <CheckboxInput
                            checked={isCloningPermitted}
                            label={t("allowCopying")}
                            onChange={handleCloningPermittedChange}
                        />
                    )}
                </div>
                <div className={`${CSS_CLASS_NAME}__actions-right`}>
                    <Button
                        accessibleText={t("cancel")}
                        onClick={handleConfirmationModalOpen}
                        style={ButtonStyles.Secondary}>
                        {t("cancel")}
                    </Button>
                    <Button
                        accessibleText={t("save")}
                        onClick={handleSaveClick}
                        style={ButtonStyles.Primary}>
                        {t("save")}
                    </Button>
                </div>
            </div>
            <ConfirmationModal
                type={modalType}
                closeButtonStyle={ModalCloseButtonStyle.Hidden}
                onCancel={handleConfirmationModalClose}
                onConfirm={handleCancelClick}
                isVisible={isConfirmationModalOpen}
                label={t("page-access-confirmation-modal--label")}
                message={t("page-access-confirmation-modal--message")}
            />
        </Modal>
    );
};
